import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EastIcon from "@mui/icons-material/East";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import {
  OUTIL_COLORS,
  paperSx,
  tableHeaderSx,
} from "./OutilContainer.styles";

const FLOW_OPTIONS = [
  { label: "—", value: "" },
  { label: "E — Entrée", value: "E" },
  { label: "S — Sortie", value: "S" },
  { label: "E/S — Bidirectionnel", value: "E/S" },
];

const CATEGORY_STYLES = {
  controle: { background: "#eee6ff", short: "Con", text: "#6f35c5" },
  direction: { background: "#dfe9f3", short: "Dir", text: "#173d61" },
  realisation: { background: "#e1f2e9", short: "Réa", text: "#28724a" },
  support: { background: "#faeadb", short: "Sup", text: "#b45c12" },
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .trim();

const sameId = (left, right) => String(left) === String(right);

const categoryStyle = (name) =>
  CATEGORY_STYLES[normalizeText(name)] || {
    background: "#edf1f5",
    short: "Aut",
    text: "#60758a",
  };

const processLabel = (processus) =>
  `${processus.instance || "Processus"} — ${
    processus.referentiel_nom || "Sans nom"
  }`;

const MatrixCell = ({ cell, disabled, onChange }) => {
  if (disabled) {
    return (
      <Box
        aria-label="Interaction avec le même processus"
        sx={{
          alignItems: "center",
          backgroundColor: "#e8eef5",
          color: "#9bacbc",
          display: "flex",
          height: 94,
          justifyContent: "center",
        }}
      >
        —
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 0.4, minHeight: 94, p: 0.45 }}>
      <Select
        aria-label="Type de flux"
        onChange={(event) => onChange("flow", event.target.value)}
        size="small"
        sx={{ backgroundColor: "#fff", borderRadius: "4px", fontSize: 9.5, height: 28 }}
        value={cell.flow || ""}
      >
        {FLOW_OPTIONS.map((option) => (
          <MenuItem key={option.value || "empty"} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <TextField
        inputProps={{ "aria-label": "Description libre du flux" }}
        minRows={2}
        multiline
        onChange={(event) => onChange("note", event.target.value)}
        placeholder="Libre..."
        size="small"
        sx={{
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
            backgroundColor: "#fff",
            borderRadius: "4px",
            fontSize: 9.5,
            minHeight: 58,
            p: 0.65,
          },
          "& textarea": { p: "0 !important" },
        }}
        value={cell.note || ""}
      />
    </Box>
  );
};

const MatriceInteractions = ({ onChange, processusOptions = [], value }) => {
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [processToAdd, setProcessToAdd] = useState(null);
  const allProcessIds = useMemo(
    () => processusOptions.map((processus) => processus.processus_id),
    [processusOptions]
  );
  const includedIds = Array.isArray(value.matrixProcessIds)
    ? value.matrixProcessIds
    : allProcessIds;
  const orientation = value.matrixOrientation || "column";
  const matrixCells = value.matrixCells || {};

  const includedProcessus = useMemo(
    () =>
      processusOptions.filter((processus) =>
        includedIds.some((id) => sameId(id, processus.processus_id))
      ),
    [includedIds, processusOptions]
  );

  const categories = useMemo(() => {
    const categoriesById = new Map();
    includedProcessus.forEach((processus) => {
      const key = String(processus.categorie_id ?? processus.categorie_name);
      if (!categoriesById.has(key)) {
        categoriesById.set(key, {
          count: 0,
          id: key,
          name: processus.categorie_name || "Autre",
          order: Number(processus.categorie_ordre),
        });
      }
      categoriesById.get(key).count += 1;
    });
    return [...categoriesById.values()].sort(
      (left, right) =>
        (Number.isFinite(left.order) ? left.order : Number.MAX_SAFE_INTEGER) -
        (Number.isFinite(right.order) ? right.order : Number.MAX_SAFE_INTEGER)
    );
  }, [includedProcessus]);

  const visibleProcessus = useMemo(
    () =>
      categoryFilter === "all"
        ? includedProcessus
        : includedProcessus.filter((processus) =>
            sameId(processus.categorie_id, categoryFilter)
          ),
    [categoryFilter, includedProcessus]
  );

  const availableProcessus = useMemo(
    () =>
      processusOptions.filter(
        (processus) =>
          !includedIds.some((id) => sameId(id, processus.processus_id))
      ),
    [includedIds, processusOptions]
  );

  const setIncludedIds = (nextIds) => onChange("matrixProcessIds", nextIds);

  const addProcessus = () => {
    if (!processToAdd) return;
    setIncludedIds([...includedIds, processToAdd.processus_id]);
    setProcessToAdd(null);
  };

  const removeProcessus = (processusId) => {
    setIncludedIds(includedIds.filter((id) => !sameId(id, processusId)));
  };

  const getCellKey = (rowProcessus, columnProcessus) => {
    const emitter = orientation === "row" ? rowProcessus : columnProcessus;
    const receiver = orientation === "row" ? columnProcessus : rowProcessus;
    return `${emitter.processus_id}:${receiver.processus_id}`;
  };

  const updateCell = (cellKey, field, nextValue) => {
    onChange("matrixCells", {
      ...matrixCells,
      [cellKey]: { ...(matrixCells[cellKey] || {}), [field]: nextValue },
    });
  };

  const orientationLabel =
    orientation === "row" ? "Ligne = Émetteur" : "Colonne = Émetteur";

  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      <Paper elevation={0} role="region" aria-label="Options de la matrice" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box
          sx={{
            alignItems: { lg: "center" },
            backgroundColor: OUTIL_COLORS.panelHeader,
            borderBottom: `1px solid ${OUTIL_COLORS.border}`,
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 1.2,
            justifyContent: "space-between",
            px: 1.75,
            py: 1.2,
          }}
        >
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <SettingsOutlinedIcon sx={{ fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
            Options — Matrice d'interactions
          </Typography>
          <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 0.7 }}>
            <Button
              onClick={() => onChange("matrixOrientation", orientation === "row" ? "column" : "row")}
              size="small"
              startIcon={orientation === "row" ? <SwapVertIcon /> : <EastIcon />}
              sx={{ backgroundColor: OUTIL_COLORS.navy, fontSize: 10.5, fontWeight: 700, textTransform: "none" }}
              variant="contained"
            >
              {orientationLabel}
            </Button>
            <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>Filtrer catégorie :</Typography>
            <Chip
              clickable
              label={`Tous (${includedProcessus.length})`}
              onClick={() => setCategoryFilter("all")}
              size="small"
              sx={{ backgroundColor: categoryFilter === "all" ? OUTIL_COLORS.navy : "#fff", color: categoryFilter === "all" ? "#fff" : OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700 }}
            />
            {categories.map((category) => (
              <Chip
                clickable
                key={category.id}
                label={`${category.name} (${category.count})`}
                onClick={() => setCategoryFilter(category.id)}
                size="small"
                sx={{ backgroundColor: categoryFilter === category.id ? OUTIL_COLORS.navy : "#fff", border: `1px solid ${OUTIL_COLORS.border}`, color: categoryFilter === category.id ? "#fff" : OUTIL_COLORS.textMuted, fontSize: 10, fontWeight: 700 }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ alignItems: { sm: "center" }, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, px: 1.75, py: 1.4 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={700}>
            <AddIcon sx={{ fontSize: 16, mr: 0.4, verticalAlign: "middle" }} />
            Ajouter un processus :
          </Typography>
          {availableProcessus.length === 0 && (
            <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
              Tous les processus du référentiel sont déjà présents.
            </Typography>
          )}
          <Autocomplete
            disabled={availableProcessus.length === 0}
            getOptionLabel={processLabel}
            isOptionEqualToValue={(option, selected) => sameId(option.processus_id, selected.processus_id)}
            onChange={(_, option) => setProcessToAdd(option)}
            options={availableProcessus}
            renderInput={(params) => <TextField {...params} placeholder="Nom du processus..." size="small" />}
            sx={{ minWidth: { xs: "100%", sm: 260 }, "& .MuiInputBase-root": { fontSize: 11, height: 36 } }}
            value={processToAdd}
          />
          <Button disabled={!processToAdd} onClick={addProcessus} size="small" startIcon={<AddIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10.5, fontWeight: 700, textTransform: "none" }}>
            Ajouter
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#e6edf5", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "6px", display: "flex", minHeight: 38, px: 1.5 }}>
        <Typography color={OUTIL_COLORS.navy} fontSize={10.5}>
          E = Entrée · S = Sortie · E/S = Bidirectionnel · Le sens du flux est déterminé par l'axe émetteur sélectionné.
        </Typography>
      </Paper>

      <Paper elevation={0} role="region" aria-label="Matrice d'interactions" sx={{ ...paperSx, overflow: "hidden", p: 1.5 }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, display: "flex", minHeight: 40, px: 1.2 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <SwapVertIcon sx={{ fontSize: 16, mr: 0.6, verticalAlign: "middle" }} />
            Matrice d'interactions
          </Typography>
        </Box>
        {visibleProcessus.length === 0 ? (
          <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 180 }}>
            <Typography color={OUTIL_COLORS.textMuted} fontSize={11}>Aucun processus pour cette catégorie.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 620, overflow: "auto" }}>
            <Table stickyHeader aria-label="Matrice des flux entre processus" size="small" sx={{ minWidth: 260 + visibleProcessus.length * 112, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableHeaderSx, left: 0, minWidth: 240, position: "sticky", width: 240, zIndex: 4 }}>
                    {orientation === "row" ? "Émetteur ↓ / Récepteur →" : "Récepteur ↓ / Émetteur →"}
                  </TableCell>
                  {visibleProcessus.map((processus) => {
                    const style = categoryStyle(processus.categorie_name);
                    return (
                      <TableCell align="center" key={processus.processus_id} sx={{ ...tableHeaderSx, minWidth: 112, p: 0.6, width: 112 }}>
                        <Typography color="#c7d5e3" fontSize={8}>{style.short}</Typography>
                        <Tooltip title={processLabel(processus)}>
                          <Typography color="#fff" fontSize={9.5} fontWeight={700} noWrap>{processus.referentiel_nom || processus.instance}</Typography>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleProcessus.map((rowProcessus) => {
                  const style = categoryStyle(rowProcessus.categorie_name);
                  return (
                    <TableRow key={rowProcessus.processus_id}>
                      <TableCell sx={{ backgroundColor: "#f1f5f9", borderRight: `1px solid ${OUTIL_COLORS.border}`, left: 0, minWidth: 240, position: "sticky", width: 240, zIndex: 2 }}>
                        <Box sx={{ alignItems: "center", display: "flex", gap: 0.6 }}>
                          <Chip label={style.short} size="small" sx={{ backgroundColor: style.background, color: style.text, fontSize: 8.5, fontWeight: 700, height: 20 }} />
                          <Tooltip title={processLabel(rowProcessus)}><Typography color={OUTIL_COLORS.text} fontSize={10} fontWeight={700} noWrap sx={{ flex: 1 }}>{rowProcessus.referentiel_nom || rowProcessus.instance}</Typography></Tooltip>
                          <Tooltip title="Modification disponible ultérieurement"><span><IconButton disabled size="small"><EditOutlinedIcon sx={{ fontSize: 14 }} /></IconButton></span></Tooltip>
                          <Tooltip title="Retirer de la matrice"><IconButton aria-label={`Retirer ${rowProcessus.instance} de la matrice`} onClick={() => removeProcessus(rowProcessus.processus_id)} size="small" sx={{ color: "#b62424" }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                      {visibleProcessus.map((columnProcessus) => {
                        const disabled = sameId(rowProcessus.processus_id, columnProcessus.processus_id);
                        const cellKey = getCellKey(rowProcessus, columnProcessus);
                        return (
                          <TableCell key={columnProcessus.processus_id} sx={{ borderColor: OUTIL_COLORS.border, p: 0, verticalAlign: "top" }}>
                            <MatrixCell cell={matrixCells[cellKey] || {}} disabled={disabled} onChange={(field, nextValue) => updateCell(cellKey, field, nextValue)} />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default MatriceInteractions;
